
import React, { useState, useEffect, useRef } from "react";
import { 
  Dialog, 
  DialogContent
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Send, PaperclipIcon, Image as ImageIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-user";
import { toast } from "sonner";
import { 
  ChatBubble, 
  ChatBubbleAvatar,
  ChatBubbleMessage
} from "@/components/ui/chat-bubble";
import { ChatInput } from "@/components/ui/chat-input";
import { ChatMessageList } from "@/components/ui/chat-message-list";

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  message: string;
  created_at: string;
}

interface Conversation {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string;
  last_message: string;
  last_message_at: string;
  unread_count: number;
}

interface UserChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipientId: string | null;
  isMessagesView?: boolean;
}

const UserChatModal: React.FC<UserChatModalProps> = ({ isOpen, onClose, recipientId, isMessagesView = false }) => {
  const { user } = useUser();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [activeView, setActiveView] = useState(isMessagesView ? "conversations" : "chat");
  
  const handleSendMessage = async () => {
    if (!user?.id || !message.trim()) return;
    
    const targetRecipientId = recipientId || selectedConversation?.user_id;
    if (!targetRecipientId) return;
    
    setSending(true);
    try {
      // Insert message
      const { data: messageData, error: messageError } = await supabase
        .from("chat_messages")
        .insert({
          sender_id: user.id,
          recipient_id: targetRecipientId,
          message: message.trim()
        })
        .select("*")
        .single();
      
      if (messageError) throw messageError;
      
      // Check if conversation already exists
      const { data: existingConversation, error: findError } = await supabase
        .from("chat_conversations")
        .select("*")
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
        .or(`user1_id.eq.${targetRecipientId},user2_id.eq.${targetRecipientId}`)
        .maybeSingle();
      
      // Update existing or create new conversation
      if (existingConversation) {
        await supabase
          .from("chat_conversations")
          .update({
            last_message_id: messageData.id,
            last_message_at: new Date().toISOString()
          })
          .eq("id", existingConversation.id);
      } else {
        await supabase
          .from("chat_conversations")
          .insert({
            user1_id: user.id,
            user2_id: targetRecipientId,
            last_message_id: messageData.id,
            last_message_at: new Date().toISOString()
          });
      }
      
      // Add message to UI
      setMessages(prevMessages => [...prevMessages, messageData]);
      setMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Erro ao enviar mensagem");
    } finally {
      setSending(false);
    }
  };

  const loadConversations = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .rpc("get_user_conversations", { user_id_param: user.id });
      
      if (error) throw error;
      
      if (data) {
        setConversations(data as Conversation[]);
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
      toast.error("Erro ao carregar conversas");
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (selectedUserId: string | null = null) => {
    if (!user?.id) return;
    const targetUserId = selectedUserId || recipientId;
    if (!targetUserId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .or(`sender_id.eq.${targetUserId},recipient_id.eq.${targetUserId}`)
        .order("created_at");
      
      if (error) throw error;
      
      if (data) {
        setMessages(data.filter(msg => 
          (msg.sender_id === user.id && msg.recipient_id === targetUserId) || 
          (msg.sender_id === targetUserId && msg.recipient_id === user.id)
        ));
      }
      
      // Mark messages as read
      await supabase
        .from("chat_messages")
        .update({ is_read: true })
        .eq("recipient_id", user.id)
        .eq("sender_id", targetUserId);
    } catch (error) {
      console.error("Error loading messages:", error);
      toast.error("Erro ao carregar mensagens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (isMessagesView) {
        loadConversations();
      } else if (recipientId) {
        loadMessages();
        setActiveView("chat");
      }
    }
  }, [isOpen, recipientId, isMessagesView]);

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.user_id);
    setActiveView("chat");
  };

  // Listen for new messages
  useEffect(() => {
    if (!user?.id || !isOpen) return;
    
    const subscription = supabase
      .channel('chat_messages_changes')
      .on('postgres_changes', {
        event: 'INSERT', 
        schema: 'public', 
        table: 'chat_messages',
        filter: `recipient_id=eq.${user.id}`
      }, (payload) => {
        // Check if message belongs to current conversation
        const newMessage = payload.new as Message;
        const currentRecipientId = recipientId || selectedConversation?.user_id;
        
        if (newMessage.sender_id === currentRecipientId) {
          setMessages(prev => [...prev, newMessage]);
          
          // Mark as read immediately
          supabase
            .from("chat_messages")
            .update({ is_read: true })
            .eq("id", newMessage.id);
        } else if (isMessagesView) {
          // Refresh conversation list if in messages view
          loadConversations();
        }
      })
      .subscribe();
    
    return () => {
      subscription.unsubscribe();
    };
  }, [user?.id, isOpen, recipientId, selectedConversation?.user_id, isMessagesView]);

  const renderChatView = () => (
    <div className="flex flex-col h-[60vh]">
      {isMessagesView && selectedConversation && (
        <div className="flex items-center p-3 border-b">
          <Button 
            variant="ghost"
            size="sm"
            onClick={() => {
              setActiveView("conversations");
              setSelectedConversation(null);
            }}
          >
            Voltar
          </Button>
          <div className="ml-2">
            <h3 className="font-medium">{selectedConversation.username}</h3>
          </div>
        </div>
      )}
      
      <div className="flex-1 overflow-hidden">
        <ChatMessageList>
          {messages.map((msg) => (
            <ChatBubble
              key={msg.id}
              variant={msg.sender_id === user?.id ? "sent" : "received"}
            >
              <ChatBubbleAvatar
                src={msg.sender_id === user?.id ? user.avatar_url || "" : selectedConversation?.avatar_url || ""}
                fallback={msg.sender_id === user?.id ? (user?.username?.[0]?.toUpperCase() || "U") : selectedConversation?.username?.[0]?.toUpperCase() || "U"}
              />
              <ChatBubbleMessage
                variant={msg.sender_id === user?.id ? "sent" : "received"}
              >
                {msg.message}
              </ChatBubbleMessage>
            </ChatBubble>
          ))}
          
          {loading && (
            <ChatBubble variant="received">
              <ChatBubbleAvatar
                fallback="?"
              />
              <ChatBubbleMessage isLoading />
            </ChatBubble>
          )}
        </ChatMessageList>
      </div>

      <div className="border-t p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
          className="relative flex gap-2"
        >
          <ChatInput
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Digite sua mensagem..."
            disabled={sending}
          />
          <Button type="submit" size="icon" disabled={sending}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );

  const renderConversationsView = () => (
    <div className="h-[60vh] overflow-y-auto p-4">
      <h2 className="text-lg font-medium mb-4">Conversas</h2>
      
      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full"></div>
        </div>
      ) : conversations.length > 0 ? (
        <div className="space-y-2">
          {conversations.map((conversation) => (
            <div 
              key={conversation.id}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer 
                ${conversation.unread_count > 0 ? 'bg-primary/5' : 'hover:bg-muted'}`}
              onClick={() => handleSelectConversation(conversation)}
            >
              <div className="relative">
                <ChatBubbleAvatar
                  src={conversation.avatar_url}
                  fallback={conversation.username?.[0]?.toUpperCase() || "U"}
                />
                {conversation.unread_count > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {conversation.unread_count}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between">
                  <p className="font-medium">{conversation.username}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(conversation.last_message_at).toLocaleDateString('pt-BR')}
                  </p>
                </div>
                <p className="text-sm text-muted-foreground truncate">
                  {conversation.last_message}
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>Nenhuma conversa encontrada</p>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        {activeView === "chat" ? renderChatView() : renderConversationsView()}
      </DialogContent>
    </Dialog>
  );
};

export default UserChatModal;
